import { Suspense, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type {
  GetV1LinksByProfileLinkResponse,
  ProfileLinkConfig,
  SkillTag,
} from "../../client";
import { LoadingSpinner } from "../../components";
import { cn } from "../../components/utils/cn";
import { useTheme } from "../../contexts";
import { useGetLeads, useGetLink, useUpdateLink } from "../../services";
import { getLeadSkillTags } from "../../utils/get-lead-skills";
import SkillPickerDrawer from "./SkillPickerDrawer";
import TemplateSkeleton from "./TemplateSkeleton";
import { useAddStackSkill } from "./useAddStackSkill";
import AccordionSection from "../dashboard/home/components/link-creator/AccordionSection";
import HighlightedProjectsLinkSettings from "../dashboard/home/components/link-creator/HighlightedProjectsLinkSettings";
import ProfileImageLinkSettings from "../dashboard/home/components/link-creator/ProfileImageLinkSettings";
import ProfileSummaryLinkSettings from "../dashboard/home/components/link-creator/ProfileSummaryLinkSettings";
import ProjectsLinkSettings from "../dashboard/home/components/link-creator/ProjectsLinkSettings";
import SkillStackLinkSettings from "../dashboard/home/components/link-creator/SkillStackLinkSettings";
import WorkExperienceLinkSettings from "../dashboard/home/components/link-creator/WorkExperienceLinkSettings";
import {
  DEFAULT_TEMPLATE_ID,
  TemplateRenderer,
  availableTemplates,
} from "../profile/templates/TemplateRenderer";
import { buildViewModel } from "../profile/viewmodel/buildViewModel";
import { resolveTokens } from "../profile/viewmodel/resolveTokens";

/**
 * TemplatePreview — an experimental editor that is meant to replace the
 * "Edit unique Profile" drawer.
 *
 * A template is always selected (the link's saved templateId, otherwise a
 * default that will become the organization's default template). The user styles
 * / configures via the drawer's side-panel settings, reused here, while a LIVE
 * render of the actual selected template updates beside the controls.
 *
 * Unlike the drawer, the "preview" is the real template render (the same pipeline
 * as /pdf), so it stays WYSIWYG across multiple templates. Edits autosave (same as
 * the drawer): each change persists immediately, with a local copy for an instant
 * preview.
 *
 * `templateId` isn't in the generated ProfileLinkConfig type yet, so it's read/
 * written through a local cast (see TemplateLinkSettings / step 6b).
 */

type WithTemplateId = { templateId?: string };

interface RouteParams {
  [key: string]: string | undefined;
  organizationId?: string;
  profileLinkId?: string;
}

function TemplateEditor({
  organizationId,
  profileLinkId,
}: {
  organizationId: string;
  profileLinkId: string;
}) {
  const { setOrganizationTheme } = useTheme();
  const { data: link } = useGetLink(profileLinkId, {
    enabled: !!profileLinkId,
  });
  const { mutate: updateLink, variables } = useUpdateLink();

  // Resolve the matched lead for this link (there's no by-link endpoint), so the
  // skills editor can surface the assignment gap. A link belongs to a lead either
  // directly (profileLinkId) or as one of its profile versions.
  const { data: leads } = useGetLeads(organizationId);
  const lead = leads.find(
    (l) =>
      l.profileLinkId === profileLinkId ||
      l.profileVersions?.some((v) => v.profileLinkId === profileLinkId),
  );
  const nonMatchingSkillTags = lead?.nonMatchingSkillTags ?? [];
  const leadSkillTags = getLeadSkillTags(lead);

  // Working config = the saved link config overlaid with the pending mutation's
  // body (optimistic) — the drawer's pattern. No separate useState, so the
  // post-save refetch (useUpdateLink invalidates getLink) can't reset an edit
  // back to the server value (which caused the pitch to flash then revert).
  const config = useMemo<ProfileLinkConfig>(() => {
    const saved =
      (link.accountProfile.link_config_data as ProfileLinkConfig) ??
      ({} as ProfileLinkConfig);
    if (variables?.body && variables.path.profileLink === profileLinkId) {
      return { ...saved, ...(variables.body as ProfileLinkConfig) };
    }
    return saved;
  }, [link, variables, profileLinkId]);

  // Autosave: every change persists immediately (same behaviour as the drawer).
  const updateConfig = (payload: Partial<ProfileLinkConfig>) => {
    updateLink(
      {
        path: { profileLink: profileLinkId },
        body: { ...config, ...payload },
      },
      { onError: () => toast.error("Could not save") },
    );
  };

  // Falls back to the default when the link has no explicit choice. This is the
  // stand-in for the organization's default template (coming later) — swap the
  // fallback for the org default once that field exists.
  const templateId =
    (config as WithTemplateId).templateId ?? DEFAULT_TEMPLATE_ID;

  // Synthesize a link carrying the current config, so the preview renders exactly
  // what is saved — through the same pipeline as the PDF.
  const previewLink = useMemo<GetV1LinksByProfileLinkResponse>(
    () => ({
      ...link,
      accountProfile: { ...link.accountProfile, link_config_data: config },
    }),
    [link, config],
  );
  const vm = buildViewModel(previewLink);
  const tokens = resolveTokens(vm);

  // Mirror the PDF route: feed the org theme into context for a faithful preview.
  useEffect(() => {
    if (vm.theme) {
      setOrganizationTheme({
        organizationId: vm.theme.organizationId ?? "",
        organizationName: vm.theme.organizationName ?? "",
        colors: vm.theme.colors,
        logoId: vm.theme.logoId,
        iconLogoId: vm.theme.iconLogoId,
      });
    }
  }, [vm.theme, setOrganizationTheme]);

  const selectTemplate = (id: string) =>
    updateConfig({ templateId: id } as Partial<ProfileLinkConfig>);

  const [isMallExpanded, setIsMallExpanded] = useState(true);
  const selectedTemplateName = availableTemplates.find(
    (t) => t.id === templateId,
  )?.name;

  const [skillPickerOpen, setSkillPickerOpen] = useState(false);
  const topSkills = config.profileSummaryConfig?.topSkills ?? [];
  const addTopSkill = (tag: SkillTag) => {
    if (topSkills.some((s) => s.name.toLowerCase() === tag.name.toLowerCase()))
      return;
    updateConfig({
      profileSummaryConfig: {
        showProfileSummary:
          config.profileSummaryConfig?.showProfileSummary ?? false,
        ...config.profileSummaryConfig,
        topSkills: [...topSkills, tag],
      },
    });
  };
  const removeTopSkill = (name: string) => {
    updateConfig({
      profileSummaryConfig: {
        showProfileSummary:
          config.profileSummaryConfig?.showProfileSummary ?? false,
        ...config.profileSummaryConfig,
        topSkills: topSkills.filter((s) => s.name !== name),
      },
    });
  };

  // Main skill stack: the picker shares the same categorise-and-add logic used
  // for free-text/missing adds, and removal mirrors the inline chip removal.
  const [stackPickerOpen, setStackPickerOpen] = useState(false);
  const addStackSkill = useAddStackSkill(config, updateConfig);
  const stackSkills = (config.skillsSummaryConfig?.skillCategoryBlocks ?? [])
    .flatMap((b) => b.skillTags)
    .sort((a, b) => a.name.localeCompare(b.name));
  const removeStackSkill = (name: string) => {
    const blocks = config.skillsSummaryConfig?.skillCategoryBlocks ?? [];
    updateConfig({
      skillsSummaryConfig: {
        ...config.skillsSummaryConfig,
        skillCategoryBlocks: blocks.map((b) => ({
          ...b,
          skillTags: b.skillTags.filter((s) => s.name !== name),
        })),
      },
    });
  };
  // Assignment-gap skills not already on the CV — surfaced as quick-add chips.
  const stackNames = new Set(stackSkills.map((s) => s.name.toLowerCase()));
  const missingStackSkills: SkillTag[] = [];
  const seenMissing = new Set<string>();
  for (const tag of [...nonMatchingSkillTags, ...leadSkillTags]) {
    const key = tag.name.toLowerCase();
    if (stackNames.has(key) || seenMissing.has(key)) continue;
    seenMissing.add(key);
    missingStackSkills.push(tag);
  }

  return (
    <div className="flex flex-row">
      {/* Controls — sticky and self-scrolling, independent of the preview which
          scrolls with the page (the dashboard layout has no bounded height). */}
      <div className="sticky top-0 flex max-h-screen w-80 shrink-0 flex-col self-start overflow-hidden border-r border-gray-3 bg-white">
        <div className="flex items-center justify-between border-b border-gray-3 px-4 py-3">
          <span className="text-sm font-semibold">Edit</span>
          <span className="text-xs text-gray-5">Saved automatically</span>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-4">
          <AccordionSection
            title="Template"
            subtitle={selectedTemplateName}
            expanded={isMallExpanded}
            onToggle={() => setIsMallExpanded((v) => !v)}
          >
            <div className="grid grid-cols-3 gap-2 p-2">
              {availableTemplates.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => selectTemplate(t.id)}
                  className="flex flex-col items-center gap-1.5 text-center"
                >
                  <div
                    className={cn(
                      "w-full rounded-md border-2 p-0.5 transition-colors",
                      templateId === t.id
                        ? "border-op-blue"
                        : "border-gray-3 hover:border-gray-4",
                    )}
                  >
                    <TemplateSkeleton id={t.id} />
                  </div>
                  <span
                    className={cn(
                      "text-[11px] font-medium",
                      templateId === t.id ? "text-op-blue" : "text-gray-6",
                    )}
                  >
                    {t.name}
                  </span>
                </button>
              ))}
            </div>
          </AccordionSection>

          {/* Reused drawer side-panel settings (component-internal styling,
              template-agnostic). Skill placement is omitted — the template owns it,
              and the summary box color is omitted — the template owns that surface. */}
          <ProfileSummaryLinkSettings
            linkConfig={config}
            onUpdate={updateConfig}
            onGenerateNew={() => {}}
            hideBoxColor
            hideGenerate
          />
          <ProfileImageLinkSettings linkConfig={config} onUpdate={updateConfig} />
          <SkillStackLinkSettings
            linkConfig={config}
            onUpdate={updateConfig}
            hidePlacement
            useLayoutSetting
          />
          <WorkExperienceLinkSettings
            linkConfig={config}
            onUpdate={updateConfig}
          />
          <ProjectsLinkSettings linkConfig={config} onUpdate={updateConfig} />
          {config.highlightedProjects &&
            config.highlightedProjects.length > 0 && (
              <HighlightedProjectsLinkSettings
                linkConfig={config}
                onUpdate={updateConfig}
              />
            )}
        </div>
      </div>

      {/* Live preview — the real template render. Scrolls with the page. */}
      <div className="flex-1 bg-gray-1 px-4 py-8">
        <div
          className="mx-auto bg-white shadow-lg"
          style={{
            maxWidth: 800,
            width: "100%",
            minHeight: 1123,
            padding: "40px 32px",
          }}
        >
          <TemplateRenderer
            templateId={templateId}
            vm={vm}
            tokens={tokens}
            pdfMode={false}
            showBranding
            onEditPitch={(value) => updateConfig({ editedPitch: value })}
            onEditName={(value) =>
              updateConfig({ editedName: value } as Partial<ProfileLinkConfig>)
            }
            onEditTitle={(value) =>
              updateConfig({ editedTitle: value } as Partial<ProfileLinkConfig>)
            }
            onEditIntro={(value) => updateConfig({ assignmentPitch: value })}
            onEditHighlight={(index, value) => {
              const highlights = [...(config.highlights ?? [])];
              highlights[index] = value;
              updateConfig({ highlights });
            }}
            onEditWorkplace={(id, patch) => {
              const wp = vm.workplaces.find((w) => w.id === id);
              if (!wp) return;
              const edited = [...(config.editedWorkplaces ?? [])];
              const idx = edited.findIndex((w) => w.id === id);
              const base = idx >= 0 ? edited[idx] : wp;
              const updated = { ...base, ...patch };
              if (idx >= 0) edited[idx] = updated;
              else edited.push(updated);
              updateConfig({ editedWorkplaces: edited });
            }}
            onRemoveSkill={removeStackSkill}
            onAddStackSkill={() => setStackPickerOpen(true)}
            onAddTopSkill={() => setSkillPickerOpen(true)}
            onRemoveTopSkill={removeTopSkill}
          />
        </div>
      </div>

      <SkillPickerDrawer
        open={skillPickerOpen}
        onOpenChange={setSkillPickerOpen}
        addedSkills={topSkills}
        onAddSkill={addTopSkill}
        onRemoveSkill={removeTopSkill}
      />

      <SkillPickerDrawer
        open={stackPickerOpen}
        onOpenChange={setStackPickerOpen}
        addedSkills={stackSkills}
        onAddSkill={addStackSkill}
        onRemoveSkill={removeStackSkill}
        suggestedSkills={missingStackSkills}
        suggestedLabel="Missing skills"
      />
    </div>
  );
}

export default function TemplatePreview() {
  const { organizationId, profileLinkId } = useParams<RouteParams>();

  if (!organizationId || !profileLinkId) {
    return <div className="p-8 text-gray-6">Missing profile link id.</div>;
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner />
        </div>
      }
    >
      <TemplateEditor
        organizationId={organizationId}
        profileLinkId={profileLinkId}
      />
    </Suspense>
  );
}
